import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useCurrentUser } from '@/features/profile/hooks/useCurrentUser'
import supportService from '../services/supportService'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { trackContactSupportClicked } from '@/shared/utils/analytics'
import type {
  FeedbackType,
  FeedbackCategory,
  FeedbackPriority,
  SubmitFeedbackRequest,
} from '../types'
import { FEEDBACK_CATEGORY_LABELS, FEEDBACK_PRIORITY_LABELS } from '../types'

// WhatsApp icon component
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

interface FormData {
  type: FeedbackType
  category: FeedbackCategory
  priority: FeedbackPriority
  subject: string
  message: string
}

interface FormErrors {
  subject?: string
  message?: string
}

export default function ContactUsPage() {
  const { user } = useAuthStore()
  const { user: userProfile } = useCurrentUser()
  const [submittedRef, setSubmittedRef] = useState<string | null>(null)

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('contact', undefined, 'Contact Us')
  }, [])

  const [formData, setFormData] = useState<FormData>({
    type: 'FEEDBACK',
    category: 'OTHER',
    priority: 'MEDIUM',
    subject: '',
    message: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})

  const submitMutation = useMutation({
    mutationFn: (data: SubmitFeedbackRequest) => supportService.submitFeedback(data),
    onSuccess: (response) => {
      setSubmittedRef(response.data.referenceNumber)
      setFormData({
        type: 'FEEDBACK',
        category: 'OTHER',
        priority: 'MEDIUM',
        subject: '',
        message: '',
      })
      setErrors({})
    },
  })

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (formData.subject.length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters'
    } else if (formData.subject.length > 200) {
      newErrors.subject = 'Subject must be less than 200 characters'
    }

    if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters'
    } else if (formData.message.length > 5000) {
      newErrors.message = 'Message must be less than 5000 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      trackContactSupportClicked('form')
      submitMutation.mutate(formData)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleWhatsAppClick = () => {
    trackContactSupportClicked('whatsapp')

    const userName = userProfile?.fullName || userProfile?.firstName || user?.name || 'User'
    const userEmail = userProfile?.email || user?.email || 'Not provided'

    const message = encodeURIComponent(
      `Hi, I need help with NEET Aspirants App.\n\nName: ${userName}\nEmail: ${userEmail}\n\nMy query: `
    )

    // Replace with your actual WhatsApp business number
    const whatsappNumber = '919910736578'
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`

    window.open(whatsappUrl, '_blank')
  }

  // Success state
  if (submittedRef) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Submitted Successfully!
          </h2>
          <p className="text-gray-600 mb-4">
            Your reference number is:
          </p>
          <p className="text-lg font-mono font-bold text-blue-600 bg-blue-50 py-2 px-4 rounded-lg mb-4">
            {submittedRef}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            A confirmation email has been sent. We'll get back to you soon.
          </p>
          <button
            onClick={() => setSubmittedRef(null)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Submit Another
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-blue-600" />
          Contact Us
        </h1>
        <p className="text-gray-600 mt-1">
          Have feedback or facing an issue? We're here to help.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Feedback Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Submit Feedback or Complaint
            </h2>

            {submitMutation.isError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Failed to submit</p>
                  <p className="text-sm text-red-700">
                    Please try again or contact us via WhatsApp.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="FEEDBACK"
                      checked={formData.type === 'FEEDBACK'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Feedback / Suggestion</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="COMPLAINT"
                      checked={formData.type === 'COMPLAINT'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Complaint / Issue</span>
                  </label>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {(Object.entries(FEEDBACK_CATEGORY_LABELS) as [FeedbackCategory, string][]).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {(Object.entries(FEEDBACK_PRIORITY_LABELS) as [FeedbackPriority, string][]).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={
                    formData.type === 'COMPLAINT'
                      ? 'Briefly describe the issue...'
                      : 'Briefly describe your feedback...'
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.subject ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder={
                    formData.type === 'COMPLAINT'
                      ? 'Please describe the issue in detail. Include any error messages, steps to reproduce, etc.'
                      : 'Please share your feedback, suggestions, or feature requests in detail.'
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                    errors.message ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* WhatsApp Contact */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Support
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Need immediate help? Chat with us on WhatsApp for faster response.
            </p>
            <button
              onClick={handleWhatsAppClick}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </button>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Available Mon-Sat, 9 AM - 6 PM
            </p>
          </div>

          {/* Additional Info */}
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-6 mt-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              Response Time
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Complaints: Within 24 hours</li>
              <li>• Feedback: Within 48 hours</li>
              <li>• Payment issues: Priority handling</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
